/**
 * 모든 페이지를 감싸는 최상위 앱 셸(app shell). 여기서 한 번만 올라가는 것들:
 * 전역 상태(RecoilRoot), 데이터 캐시 기본값(SWRConfig), 세션 확인(SessionManager),
 * 초기 비밀번호 강제 변경 게이트(PasswordChangeGate), 네트워크 상태 배너,
 * 감사 로그 전송(AuditLogSync), 로그인 이탈 방지 모달(TrialBlockModal), Hotjar 스크립트.
 *
 * 렌더 순서(코드 위에서 아래로):
 *  1. RecoilRoot 로 전체를 감싼다.
 *  2. isMounted 가 true(마운트 이후)일 때만 SessionManager·PasswordChangeGate·NetworkStatusBanner·
 *     AuditLogSync 렌더.
 *     이 게이트 때문에 첫 렌더에서는 세션 확인이 비어 있는 채로 하위 페이지의 fetch 가 먼저 돌 수 있다.
 *  3. SWRConfig 로 감싼 뒤 그 안에서 실제 페이지(Component)를 렌더.
 *  4. isMounted 이후에 TrialBlockModal.
 *  5. Hotjar 인라인 스크립트(NEXT_PUBLIC_HOTJAR_ENABLED=true 일 때만, hjid 5195906). GA4 gtag(NEXT_PUBLIC_GA_ID 있을 때만).
 *
 * SWR 기본 설정 의미:
 *  - dedupingInterval: 10000 → 같은 요청을 10초 안에 다시 보내면 새로 안 보내고 기존 응답을 재사용한다.
 *  - revalidateOnFocus: false → 브라우저 탭에 다시 돌아와도 자동으로 재요청하지 않는다.
 *    그래서 화면 어딘가에서 새로고침을 의도했다면 `mutate(key)`를 직접 불러야 한다.
 *  - suspense: false → 데이터가 아직 없을 때 화면이 멈추지 않으므로, 각 컴포넌트가
 *    `data === undefined` 분기를 직접 처리해야 한다.
 */
import { SWRConfig } from "swr";
import type { AppProps } from "next/app";
import { useEffect, useState } from "react";
import { handleAPIError } from "@/utils/handleAPIError";
import { RecoilRoot } from "recoil";
import { useRouter } from "next/router";
import Head from "next/head";
import { GA_ID, trackPageView } from "@/utils/analytics";
import SessionManager from "@/components/auth/SessionManager";
import PasswordChangeGate from "@/components/auth/PasswordChangeGate";
import NetworkStatusBanner from "@/components/common/NetworkStatusBanner";
import { AuditLogSync } from "@/components/common/AuditLogSync";
import TrialBlockModal from "@/components/modal/TrialBlockModal";

import "../styles/global.css";
import "../styles/custom.scroll.css";
import "../styles/MyDatePicker.css";
import "@wanteddev/wds/theme.css";

/** 정식 도메인. canonical·og:url·og:image 의 기준. dq.ai.kr 등 다른 호스트로 접속해도 이 값으로 고정한다(중복 콘텐츠 방지). */
const SITE_ORIGIN = "https://haebom.ai.kr";
const DEFAULT_DESCRIPTION =
  "AI시대 새로운 진로탐색의 시작, 해봄(구 디큐 DQ). 길잡이별 검사(논·서술형 AI 역량 진단)와 맞춤 학습으로 초·중·고 학생의 미래 역량을 키우는 진로탐색 학습 플랫폼입니다.";
const DEFAULT_OG_DESCRIPTION = "길잡이별 검사로 시작하는 AI시대 진로탐색 — 해봄";

/**
 * 현재 경로의 canonical URL. 쿼리·해시는 버리고 정식 도메인을 붙인다.
 * 동적 라우트(`[id]`)는 빌드 시 경로가 확정되지 않으므로 canonical 을 내지 않는다.
 */
function buildCanonicalUrl(asPath: string): string | null {
  const path = asPath.split(/[?#]/)[0] || "/";
  if (path.includes("[")) return null;
  return path === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path.replace(/\/+$/, "")}`;
}

/** Next.js가 모든 페이지 전환마다 다시 그리는 루트 컴포넌트. 위 파일 TSDoc의 순서대로 조립한다. */
export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const canonicalUrl = buildCanonicalUrl(router.asPath);

  // GA4 페이지뷰: 첫 렌더 + 클라이언트 라우트 전환. gtag config 는 send_page_view:false 라 여기서만 보낸다.
  useEffect(() => {
    if (!GA_ID) return;
    trackPageView(router.asPath);
    const onRouteChange = (url: string) => trackPageView(url);
    router.events.on("routeChangeComplete", onRouteChange);
    return () => router.events.off("routeChangeComplete", onRouteChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 라우트별 기본 제목. 위에서부터 먼저 맞는 항목을 쓰므로 구체적인 경로를 앞에 둔다.
  // 페이지가 자체 <Head><title>을 두면 그쪽이 이 값을 덮어쓴다.
  const TITLE_RULES: Array<[(path: string) => boolean, string]> = [
    // 검색 결과 제목으로 쓰이는 페이지라 브랜드 + 서비스 설명 키워드 포함
    [(x) => x === "/", "해봄 - AI 진로탐색 학습 플랫폼"],
    [(x) => x === "/login", "해봄 - 로그인"],
    [(x) => x === "/password-setup", "해봄 - 비밀번호 설정"],
    [(x) => x.startsWith("/signin/terms"), "해봄 - 약관 동의"],
    [(x) => x.startsWith("/signin/userInfo"), "해봄 - 회원정보 입력"],
    [(x) => x.startsWith("/signin/childAuth"), "해봄 - 보호자 인증"],
    [(x) => x.startsWith("/signin/complete"), "해봄 - 가입 완료"],
    [(x) => x.startsWith("/signin"), "해봄 - 회원가입"],
    [(x) => x.startsWith("/mypage"), "해봄 - 학습 리포트"],
    [(x) => x.startsWith("/my"), "해봄 - 마이페이지"],
    [(x) => x.startsWith("/essay-learning"), "해봄 - 논서술형 학습"],
    [(x) => x.startsWith("/problem"), "해봄 - 문제 풀이"],
    [(x) => x.startsWith("/help"), "해봄 - 고객센터"],
    [(x) => x.startsWith("/teacher"), "해봄 - 선생님 페이지"],
    [(x) => x.startsWith("/find"), "해봄 - 아이디·비밀번호 찾기"],
    [(x) => x.startsWith("/privacy"), "해봄 - 개인정보처리방침"],
  ];

  const pageTitle =
    TITLE_RULES.find(([match]) => match(router.pathname))?.[1] ?? "해봄";

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    handleAPIError();
  }, []);

  useEffect(() => {
    // 오늘의 문제 풀이 페이지에는 pull-to-refresh를 막는 스타일링 부여
    const bodyClassMap: Record<string, string> = {
      "/problem/list": "problem-body",
    };

    const body = document.querySelector("body");
    if (body) {
      Object.values(bodyClassMap).forEach((cls) => body.classList.remove(cls)); // 기존 클래스 제거
      if (bodyClassMap[router.pathname]) {
        body.classList.add(bodyClassMap[router.pathname]); // 현재 페이지 클래스 추가
      }
    }
  }, [router.pathname]);

  return (
    <RecoilRoot>
      {isMounted && (
        <>
          <SessionManager />
          <PasswordChangeGate />
          <NetworkStatusBanner />
          <AuditLogSync />
        </>
      )}
      <SWRConfig
        value={{
          suspense: false,
          revalidateOnFocus: false,
          dedupingInterval: 10000,
        }}
      >
        <Head>
          <title>{pageTitle}</title>
          {/* SEO 기본값. 페이지가 같은 key 로 <Head> 를 두면 그쪽이 이긴다(랜딩 등). */}
          <meta key="description" name="description" content={DEFAULT_DESCRIPTION} />
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:site_name" property="og:site_name" content="해봄" />
          <meta key="og:locale" property="og:locale" content="ko_KR" />
          <meta key="og:title" property="og:title" content={pageTitle} />
          <meta key="og:description" property="og:description" content={DEFAULT_OG_DESCRIPTION} />
          <meta key="og:image" property="og:image" content={`${SITE_ORIGIN}/images/og-image.png`} />
          <meta key="og:image:width" property="og:image:width" content="1200" />
          <meta key="og:image:height" property="og:image:height" content="630" />
          {canonicalUrl && <link key="canonical" rel="canonical" href={canonicalUrl} />}
          {canonicalUrl && <meta key="og:url" property="og:url" content={canonicalUrl} />}
        </Head>
        <Component {...pageProps} />
        {/* GA4 gtag 로더는 _document.tsx 에 있다(정적 HTML 포함). 페이지뷰는 위 useEffect 가 보낸다. */}
      </SWRConfig>
      {isMounted && <TrialBlockModal />}
      {process.env.NEXT_PUBLIC_HOTJAR_ENABLED === "true" && (
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(h,o,t,j,a,r){
  h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
  h._hjSettings={hjid:5195906,hjsv:6};
  a=o.getElementsByTagName('head')[0];
  r=o.createElement('script');r.async=1;
  r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
  a.appendChild(r);
})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
          }}
        ></script>
      )}
    </RecoilRoot>
  );
}
